import './MediaList.scss';

import { AsyncStateNotification } from '@ui/notifications';
import { createSonarSelector } from '@lib/selectors';
import { InitiativeMediaListItem } from './MediaListItem';
import { LinkModal } from '@ui/LinkModal';
import { useParams } from 'react-router-dom';

import { Collapse, List } from 'antd';
import { COPY_SPECIFIC, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { coreActions, InitiativeFile, InitiativeLink } from '@sonar-software/sonar-core';
import { FileModal, FileModalFormValues } from '@ui/FileModal';
import React, { useState } from 'react';
import { useAction, useSonarSelector } from '@hooks';

export type ListItem = InitiativeFile | InitiativeLink;

interface InitiativesMediaListProps {
	listSelected: string;
	showList: boolean;
}

const getFilesDataSelector = createSonarSelector((state) => state.core.initiatives.files.get.data);
const getLinksDataSelector = createSonarSelector((state) => state.core.initiatives.links.get.data);
const postFilesStateSelector = createSonarSelector((state) => state.core.initiatives.files.post);

export const InitiativeMediaList: React.FC<InitiativesMediaListProps> = ({ listSelected, showList }) => {
	const [ selectedItem, setSelectedItem ] = useState<ListItem | null>();

	const params = useParams<{ id: string }>();
	const initiativeId = Number(params.id);

	const files = useSonarSelector(getFilesDataSelector);
	const links = useSonarSelector(getLinksDataSelector);

	const updateFile = useAction(coreActions.requestPatchCoreInitiativesFilesById);
	const updateLink = useAction(coreActions.requestPatchCoreInitiativesLinksById);

	const handleCloseModal = () => {
		setSelectedItem(null);
	};

	const handleUpdateFile = (updatedFile: FileModalFormValues) => {
		if (selectedItem) {
			const { name } = updatedFile;
			const { id } = selectedItem;

			updateFile({
				id,
				initiativeId,
				name
			});
		}

		handleCloseModal();
	};

	const handleUpdateLink = (updatedLink: { title: string; url: string }) => {
		if (selectedItem) {
			const { title, url } = updatedLink;
			const { id } = selectedItem;

			updateLink({
				id,
				initiativeId,
				title,
				url
			});
		}
		handleCloseModal();
	};

	const dataSource = listSelected === 'files' ? files?.results : links?.results;
	const showFileModal = Boolean((selectedItem as InitiativeFile)?.contentType);
	const showLinkModal = Boolean(selectedItem && !showFileModal);

	const renderItem = (item: ListItem) =>
		<InitiativeMediaListItem
			listItem={ item }
			setSelectedItem={ setSelectedItem } />;

	return (
		<>
			{ links && files &&
				<Collapse
					activeKey={ showList ? [ 1 ] : [] }
					className='media-list'
					ghost
					items={ [ {
						children: <List
							dataSource={ dataSource }
							itemLayout='horizontal'
							locale={ { emptyText: COPY_SPECIFIC.CLICK_TO_ADD_LINKS_AND_FILES_TO_INITIATIVE } }
							renderItem={ renderItem }
							size='small' />,
						key: 1,
						label: '',
						showArrow: false
					} ] } /> }
			<FileModal
				file={ selectedItem as InitiativeFile }
				onCancel={ handleCloseModal }
				onOk={ handleUpdateFile }
				open={ showFileModal } />
			<LinkModal
				link={ selectedItem as InitiativeLink }
				onCancel={ handleCloseModal }
				onOk={ handleUpdateLink }
				open={ showLinkModal } />
			<AsyncStateNotification
				errored={ NOTIFICATIONS_COPY.ERROR_GET_INITIATIVES_BY_ID_FILES_DOWNLOAD }
				failed={ NOTIFICATIONS_COPY.FAILED_GET_INITIATIVES_BY_ID_FILES_DOWNLOAD }
				resetRawAction={ coreActions.resetPostCoreInitiativesFiles }
				stateSelector={ postFilesStateSelector } />
		</>
	);
};
